import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { KOFI_CONFIG } from '../config/kofi';

const FREE_SCAN_LIMIT = KOFI_CONFIG.scanLimits.free;
const CONTRIBUTOR_SCAN_LIMIT = KOFI_CONFIG.scanLimits.contributor;
const SUBSCRIPTIONS_TABLE = 'subscriptions';
const SUPPORTERS_TABLE = 'supporters';
const CONTRIBUTOR_PROVIDERS = ['kofi', 'stripe', 'monthly_pick_reward'];

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function isContributorActiveStatus({ isSupporter, scanLimit }) {
  if (!isSupporter) return false;
  const resolvedLimit = Number(scanLimit || 0);
  return resolvedLimit >= CONTRIBUTOR_SCAN_LIMIT;
}

// Ko-fi monthly period + 3-day grace. Matches the backend constant.
const KOFI_PERIOD_DAYS = 33;

function isSubscriptionActive(row) {
  if (!row) return false;
  const status = String(row.status || '').trim().toLowerCase();
  const provider = String(row.provider || '').trim().toLowerCase();
  const hasPeriodEnd = Boolean(row.current_period_end);

  if (!['active', 'trialing', 'cancelled'].includes(status)) return false;
  if (status === 'cancelled' && !hasPeriodEnd) return false;

  if ((status === 'active' || status === 'trialing') && !hasPeriodEnd) {
    // Ko-fi does not include an expiry date in its webhooks. A subscription that
    // simply lapsed (no renewal, no explicit cancellation) stays status='active'
    // with no current_period_end. Fall back to updated_at + grace period so the
    // row expires naturally if Ko-fi stops sending renewal payments.
    if (provider === 'kofi') {
      const updatedAt = row.updated_at ? new Date(row.updated_at) : null;
      if (updatedAt && !Number.isNaN(updatedAt.getTime())) {
        const cutoff = new Date(updatedAt);
        cutoff.setDate(cutoff.getDate() + KOFI_PERIOD_DAYS);
        return cutoff > new Date();
      }
      // No updated_at — treat as inactive to be safe.
      return false;
    }
    // Non-Ko-fi providers with no period_end are treated as perpetually active
    // (e.g. admin-granted monthly_pick_reward rows).
    return true;
  }

  const end = new Date(row.current_period_end);
  if (Number.isNaN(end.getTime())) return true;
  return end > new Date();
}

async function fetchSubscriptionsForUser(user) {
  const userEmail = normalizeEmail(user?.email);
  const baseSelect = 'id,user_id,email,provider,plan_code,tier_name,status,scan_limit,current_period_end,updated_at,created_at';

  const promises = [];

  if (user?.id) {
    promises.push(
      supabase
        .from(SUBSCRIPTIONS_TABLE)
        .select(baseSelect)
        .in('provider', CONTRIBUTOR_PROVIDERS)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(10)
    );
  } else {
    promises.push(Promise.resolve({ data: [], error: null }));
  }

  if (userEmail) {
    promises.push(
      supabase
        .from(SUBSCRIPTIONS_TABLE)
        .select(baseSelect)
        .in('provider', CONTRIBUTOR_PROVIDERS)
        .eq('email', userEmail)
        .order('updated_at', { ascending: false })
        .limit(10)
    );
  } else {
    promises.push(Promise.resolve({ data: [], error: null }));
  }

  const [byUserId, byEmail] = await Promise.all(promises);

  let rows = [];

  if (!byUserId.error && Array.isArray(byUserId.data)) {
    rows = rows.concat(byUserId.data);
  } else if (byUserId.error && !String(byUserId.error.message || '').includes('does not exist')) {
    // eslint-disable-next-line no-console
    console.error('[SUPPORTER] subscriptions by user_id lookup failed:', byUserId.error);
  }

  if (!byEmail.error && Array.isArray(byEmail.data)) {
    const seen = new Set(rows.map((row) => row.id));
    byEmail.data.forEach((row) => {
      if (!seen.has(row.id)) rows.push(row);
    });
  } else if (byEmail.error && !String(byEmail.error.message || '').includes('does not exist')) {
    // eslint-disable-next-line no-console
    console.error('[SUPPORTER] subscriptions by email lookup failed:', byEmail.error);
  }

  const active = rows.filter(isSubscriptionActive);

  // Only certain rows actually grant Contributeur Bongo perks. Legacy Ko-fi
  // follower rows (status='active' with a small scan_limit and no contributor
  // tier_name) must NOT bump a free user above the free quota — that's the
  // bug that was letting free users do 4-5 scans/day. Mirrors the backend
  // _resolve_subscription_scan_access decision.
  const contributorTierNames = new Set(['contributeur bongo', 'bongo contributor']);
  const qualifying = active.filter((row) => {
    const provider = String(row?.provider || '').trim().toLowerCase();
    const tierName = String(row?.tier_name || '').trim().toLowerCase();
    if (provider === 'stripe') return true;
    if (provider === 'monthly_pick_reward') return true;
    if (provider === 'kofi' && contributorTierNames.has(tierName)) return true;
    return false;
  });

  qualifying.sort((a, b) =>
    String(b.updated_at || '').localeCompare(String(a.updated_at || ''))
  );

  return qualifying[0] || null;
}

async function fetchLegacySupporter(user) {
  const userEmail = normalizeEmail(user?.email);
  const baseSelect = 'id, user_id, email, kofi_email, tier, status, started_at, expires_at, created_at';

  let data = null;

  if (user?.id) {
    const byUserId = await supabase
      .from(SUPPORTERS_TABLE)
      .select(baseSelect)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!byUserId.error && byUserId.data) {
      data = byUserId.data;
    }
  }

  if (!data && userEmail) {
    const byEmail = await supabase
      .from(SUPPORTERS_TABLE)
      .select(baseSelect)
      .eq('email', userEmail)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!byEmail.error && byEmail.data) {
      data = byEmail.data;
    }
  }

  if (!data) return null;

  if (data.expires_at && new Date(data.expires_at) < new Date()) {
    return null;
  }

  return data;
}

// ─── In-memory cache for supporter status (TTL: 5 min) ──────────────────────
// Keyed by user.id (or the literal 'guest' for unauthenticated state) so that
// the guest result never leaks to a signed-in user. Previously, opening the
// app while signed out and then signing in returned the cached 'guest' result
// on subsequent fetches — symptom : on a fresh device, the contributor badge
// would not appear right after sign in.
const _supporterStatusCacheByUserId = new Map(); // key -> { result, at }
const SUPPORTER_CACHE_TTL_MS = 5 * 60 * 1000;

// Subscribers (live useSupporterStatus hooks) get notified on cache invalidation
// so the badge / perks refresh immediately, instead of waiting up to 5 min for
// the TTL to expire. This is critical right after Stripe checkout success.
const _cacheListeners = new Set();

function _notifyCacheListeners() {
  _cacheListeners.forEach((fn) => {
    try {
      fn();
    } catch (_error) {
      // Ignore listener errors so one bad subscriber can't break the others.
    }
  });
}

function _getCachedSupporterStatus(userIdKey) {
  const entry = _supporterStatusCacheByUserId.get(userIdKey);
  if (!entry) return null;
  if (Date.now() - entry.at > SUPPORTER_CACHE_TTL_MS) {
    _supporterStatusCacheByUserId.delete(userIdKey);
    return null;
  }
  return entry.result;
}

function _setCachedSupporterStatus(userIdKey, result) {
  _supporterStatusCacheByUserId.set(userIdKey, { result, at: Date.now() });
}

export function invalidateSupporterStatusCache() {
  _supporterStatusCacheByUserId.clear();
  _notifyCacheListeners();
}
// ─────────────────────────────────────────────────────────────────────────────

export async function checkSupporterStatus() {
  // Resolve the auth user first — we need user.id to pick the right cache
  // bucket. Caching only after auth resolution prevents the "guest leaks to
  // signed-in" bug that hid the contributor badge on freshly signed-in devices.
  let user = null;
  try {
    const { data } = await supabase.auth.getUser();
    user = data?.user || null;
  } catch (_error) {
    user = null;
  }

  const userIdKey = user?.id || 'guest';
  const cached = _getCachedSupporterStatus(userIdKey);
  if (cached) return cached;

  try {
    if (!user) {
      const result = {
        isSupporter: false,
        isContributorActive: false,
        scanLimit: FREE_SCAN_LIMIT,
        tier: null,
        status: 'free',
        source: 'guest',
        expired: false,
        activeProvider: null,
        expiresAt: null,
      };
      _setCachedSupporterStatus(userIdKey, result);
      return result;
    }

    const subscription = await fetchSubscriptionsForUser(user);
    if (subscription) {
      // The presence of a qualifying subscription row is the source of truth.
      // We always grant the canonical contributor limit (10) — never trust the
      // legacy `scan_limit` column, which can hold stale 5/15 values.
      const resolvedLimit = CONTRIBUTOR_SCAN_LIMIT;
      const isSupporter = true;
      const result = {
        isSupporter,
        isContributorActive: isContributorActiveStatus({
          isSupporter,
          scanLimit: resolvedLimit,
        }),
        scanLimit: resolvedLimit,
        tier: subscription.tier_name || subscription.plan_code || 'contributor',
        status: subscription.status || 'active',
        source: 'subscriptions',
        since: subscription.created_at || null,
        expired: false,
        activeProvider: subscription.provider || null,
        expiresAt: subscription.current_period_end || null,
      };
      _setCachedSupporterStatus(userIdKey, result);
      return result;
    }

    const legacy = await fetchLegacySupporter(user);
    if (legacy) {
      const result = {
        isSupporter: true,
        isContributorActive: true,
        scanLimit: CONTRIBUTOR_SCAN_LIMIT,
        tier: legacy.tier || 'contributor',
        status: legacy.status || 'active',
        source: 'supporters_legacy',
        since: legacy.started_at || null,
        expired: false,
        // Legacy `supporters` table is Ko-fi era — surface 'kofi' so the UI
        // routes the user to the Ko-fi management instead of Stripe portal.
        activeProvider: 'kofi',
        expiresAt: legacy.expires_at || null,
      };
      _setCachedSupporterStatus(userIdKey, result);
      return result;
    }

    const result = {
      isSupporter: false,
      isContributorActive: false,
      scanLimit: FREE_SCAN_LIMIT,
      tier: null,
      status: 'free',
      source: 'none',
      expired: false,
      activeProvider: null,
      expiresAt: null,
    };
    _setCachedSupporterStatus(userIdKey, result);
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[SUPPORTER] status check failed:', err);
    return {
      isSupporter: false,
      isContributorActive: false,
      scanLimit: FREE_SCAN_LIMIT,
      tier: null,
      status: 'free',
      source: 'error',
      expired: false,
      activeProvider: null,
      expiresAt: null,
    };
  }
}

export async function getCurrentUserScanLimit() {
  const status = await checkSupporterStatus();
  return {
    scanLimit: status.scanLimit,
    isSupporter: status.isSupporter,
    isContributorActive: status.isContributorActive,
    tier: status.tier,
    source: status.source,
    status: status.status,
  };
}

export function useSupporterStatus() {
  const { user, loading } = useAuth();
  const [status, setStatus] = useState({
    isSupporter: false,
    isContributorActive: false,
    scanLimit: FREE_SCAN_LIMIT,
    tier: null,
    status: 'free',
    source: 'initial',
    expired: false,
    activeProvider: null,
    expiresAt: null,
    loading: true,
  });
  // Bumped whenever someone calls invalidateSupporterStatusCache(); used as
  // a useEffect dep so the hook refetches immediately instead of waiting
  // for the 5-min TTL or a user.id change.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const listener = () => setRefreshTick((tick) => tick + 1);
    _cacheListeners.add(listener);
    return () => {
      _cacheListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (loading) {
      setStatus((prev) => ({ ...prev, loading: true }));
      return () => {
        isMounted = false;
      };
    }

    checkSupporterStatus().then((result) => {
      if (!isMounted) return;
      setStatus({ ...result, loading: false });
    });

    return () => {
      isMounted = false;
    };
  }, [loading, user?.id, user?.email, refreshTick]);

  return status;
}

export default checkSupporterStatus;
